import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  EnvelopeIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  AcademicCapIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import useOrganizationSettings from '../../hooks/useOrganizationSettings';
import { APP_LOGO, APP_LOGO_ALT } from '../../constants/branding';
import notify from '../../utils/notify';

const FEATURES = [
  { icon: AcademicCapIcon, key: 'courses' },
  { icon: ChartBarIcon, key: 'reports' },
  { icon: ShieldCheckIcon, key: 'secure' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.45, ease: 'easeOut' },
  }),
};

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, loading: authLoading, sessionChecked } = useAuth();
  const { t } = useLanguage();
  const { data: orgSettings } = useOrganizationSettings({ enabled: true });

  const redirectTarget =
    location.state?.from?.pathname && location.state.from.pathname !== '/login'
      ? `${location.state.from.pathname}${location.state.from.search || ''}${location.state.from.hash || ''}`
      : '/dashboard';

  useEffect(() => {
    if (!authLoading && sessionChecked && isAuthenticated) {
      navigate(redirectTarget, { replace: true });
    }
  }, [isAuthenticated, authLoading, sessionChecked, navigate, redirectTarget]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const orgName = orgSettings?.gymName || 'FORMAPRO';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await login({ email, password });
      if (res && res.success) {
        navigate(redirectTarget, { replace: true });
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        t('login.errorGeneric');
      setError(msg);
      notify.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page relative flex min-h-screen flex-col lg:flex-row">
      {/* ── Brand hero panel ── */}
      <div className="login-hero relative overflow-hidden lg:min-h-screen lg:w-[46%] xl:w-[50%]">
        <div className="login-hero-gradient absolute inset-0" />
        <div className="login-hero-grid absolute inset-0 opacity-[0.12]" />
        <div className="login-orb login-orb-a" />
        <div className="login-orb login-orb-b" />
        <div className="login-orb login-orb-c" />

        <div className="relative z-10 flex min-h-[280px] flex-col justify-between px-6 py-10 sm:min-h-[320px] sm:px-10 lg:min-h-screen lg:p-12 xl:p-14">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white p-2 shadow-xl shadow-black/25 ring-1 ring-white/40 sm:h-16 sm:w-16">
                <img src={APP_LOGO} alt={APP_LOGO_ALT} className="h-full w-full object-contain" />
              </div>
              <div>
                <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-red-100/80">
                  <SparklesIcon className="h-3.5 w-3.5" />
                  {t('login.platformLabel')}
                </p>
                <h1 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">{orgName}</h1>
              </div>
            </div>
          </motion.div>

          <div className="mt-8 lg:mt-0">
            <motion.h2
              custom={1}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className="max-w-lg text-2xl font-bold leading-tight text-white sm:text-3xl xl:text-[2.35rem] xl:leading-[1.15]"
            >
              {t('login.heroTitle')}
            </motion.h2>
            <motion.p
              custom={2}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className="mt-4 max-w-md text-sm leading-relaxed text-red-100/75 sm:text-base"
            >
              {t('login.heroSubtitle')}
            </motion.p>

            <motion.ul
              custom={3}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className="mt-8 hidden space-y-3 sm:block lg:mt-10"
            >
              {FEATURES.map(({ icon: Icon, key }) => (
                <li
                  key={key}
                  className="flex items-center gap-3 rounded-xl bg-white/10 px-4 py-3 text-sm text-white/90 ring-1 ring-white/10 backdrop-blur-sm transition hover:bg-white/15"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-500/30 ring-1 ring-white/20">
                    <Icon className="h-4 w-4 text-white" />
                  </span>
                  {t(`login.feature_${key}`)}
                </li>
              ))}
            </motion.ul>
          </div>

          <motion.div
            custom={4}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mt-8 hidden items-center gap-6 lg:flex"
          >
            <div className="flex items-center gap-2 text-xs text-red-100/60">
              <UserGroupIcon className="h-4 w-4" />
              {t('login.trustBadge')}
            </div>
            {orgSettings?.address ? (
              <p className="text-xs text-red-100/45">{orgSettings.address}</p>
            ) : null}
          </motion.div>
        </div>
      </div>

      {/* ── Form panel ── */}
      <div className="login-form-panel relative flex flex-1 flex-col items-center justify-center px-5 pb-10 pt-0 sm:px-8 lg:py-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
          className="relative w-full max-w-[440px] -mt-10 sm:-mt-14 lg:mt-0"
        >
          <div className="login-card overflow-hidden rounded-3xl border border-red-100/80 bg-white shadow-2xl shadow-red-950/10 dark:border-red-950/40 dark:bg-slate-900 dark:shadow-black/50">
            <div className="login-card-accent h-1.5 w-full bg-gradient-to-r from-red-700 via-red-500 to-rose-400" />

            <div className="p-8 sm:p-10">
              <div className="mb-8 text-center lg:text-left">
                <div className="mb-5 flex justify-center lg:hidden">
                  <div className="flex h-12 items-center justify-center rounded-xl bg-white px-4 py-2 shadow-md ring-1 ring-red-100">
                    <img src={APP_LOGO} alt={APP_LOGO_ALT} className="h-8 w-auto object-contain" />
                  </div>
                </div>
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 ring-1 ring-red-100 dark:bg-red-950/50 dark:text-red-300 dark:ring-red-900/50 lg:hidden">
                  <ShieldCheckIcon className="h-3.5 w-3.5" />
                  {t('login.secureAccess')}
                </div>
                <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-[1.65rem]">
                  {t('login.title')}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-500 dark:text-slate-400">
                  {t('login.formSubtitle')}
                </p>
              </div>

              {error ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mb-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/60 dark:bg-red-950/50 dark:text-red-200"
                  role="alert"
                >
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-200 text-red-700 dark:bg-red-900 dark:text-red-300">
                    !
                  </span>
                  {error}
                </motion.div>
              ) : null}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label
                    htmlFor="login-email"
                    className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-300"
                  >
                    {t('login.emailLabel')}
                  </label>
                  <div className="group relative">
                    <EnvelopeIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400 transition group-focus-within:text-red-500" />
                    <input
                      id="login-email"
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@exemple.dz"
                      className="login-input w-full rounded-xl border border-slate-200 bg-slate-50/90 py-3.5 pl-12 pr-4 text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-800/70 dark:text-white dark:placeholder:text-slate-500"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="login-password"
                    className="mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-300"
                  >
                    {t('login.passwordLabel')}
                  </label>
                  <div className="group relative">
                    <LockClosedIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400 transition group-focus-within:text-red-500" />
                    <input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="login-input w-full rounded-xl border border-slate-200 bg-slate-50/90 py-3.5 pl-12 pr-12 text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-800/70 dark:text-white dark:placeholder:text-slate-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/50 dark:hover:text-red-300"
                      aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={clsx(
                    'login-submit relative mt-3 w-full overflow-hidden rounded-xl py-3.5 text-sm font-bold tracking-wide text-white',
                    'bg-gradient-to-r from-red-700 via-red-600 to-red-500',
                    'shadow-lg shadow-red-600/35',
                    'transition hover:shadow-xl hover:shadow-red-500/40 active:scale-[0.99]',
                    'focus:outline-none focus:ring-4 focus:ring-red-500/30',
                    'disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none'
                  )}
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {loading ? (
                      <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                        {t('login.loading')}
                      </>
                    ) : (
                      t('login.submit')
                    )}
                  </span>
                </button>
              </form>

              <p className="mt-8 border-t border-slate-100 pt-6 text-center text-xs leading-relaxed text-slate-400 dark:border-slate-800 dark:text-slate-500">
                {t('login.footerNote')}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
